import React, { useState, useEffect } from 'react';
import { authService } from '../services/api';
import { Copy, Check, Eye, EyeOff, Plus, Trash2, RefreshCw, X, AlertCircle } from 'lucide-react';

function Settings() {
  const [apiKey, setApiKey] = useState('');
  const [apiKeys, setApiKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [copied, setCopied] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newApiKey, setNewApiKey] = useState(null);
  const [showNewApiKey, setShowNewApiKey] = useState(true);
  const [deleteKeyId, setDeleteKeyId] = useState(null);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get legacy API key (backward compatibility)
      try {
        const legacyKeyResponse = await authService.getApiKey();
        setApiKey(legacyKeyResponse.api_key || '');
      } catch (legacyError) {
        console.warn('Legacy API key fetch failed:', legacyError);
      }
      
      // Get list of all API keys
      const keysResponse = await authService.listApiKeys();
      setApiKeys(keysResponse.keys || []);
    } catch (err) {
      console.error('Error fetching API keys:', err);
      setError('Failed to retrieve API keys. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const createApiKey = async () => {
    if (isCreating) return;
    
    try {
      setIsCreating(true);
      setError(null);
      
      const name = newKeyName.trim() || 'API Key';
      const response = await authService.createApiKey(name);
      
      // Display the new key
      setNewApiKey(response);
      setShowNewApiKey(true);
      
      // Clear input and refresh the list
      setNewKeyName('');
      fetchApiKeys();
      
      setSuccessMessage('API key created successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error creating API key:', err);
      setError('Failed to create API key. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const deleteApiKey = async (keyId) => {
    if (isDeleting) return;
    
    try {
      setIsDeleting(true);
      setError(null);
      
      await authService.deleteApiKey(keyId);
      
      // Refresh the list
      fetchApiKeys();
      setDeleteKeyId(null);
      
      setSuccessMessage('API key deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting API key:', err);
      setError('Failed to delete API key. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const copyToClipboard = (key, id = null) => {
    navigator.clipboard.writeText(key).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const toggleApiKeyVisibility = () => {
    setShowApiKey(!showApiKey);
  };

  const closeNewKeyModal = () => {
    setNewApiKey(null);
  };

  const maskApiKey = (key) => {
    if (!key) return '';
    const visibleChars = 4;
    const prefix = key.slice(0, visibleChars);
    return prefix + '•'.repeat(Math.max(0, key.length - visibleChars));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Settings</h1>

      {/* Success message */}
      {successMessage && (
        <div className="mb-4 p-3 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 text-green-800 dark:text-green-200 flex items-center text-sm">
          <Check size={16} className="mr-2 flex-shrink-0" />
          {successMessage}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 text-red-800 dark:text-red-200 flex items-center text-sm">
          <AlertCircle size={16} className="mr-2 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Legacy API Key */}
      {apiKey && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300">Legacy API Key</h2>
            <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">From Session Token</span>
          </div>
          
          {isLoading ? (
            <div className="animate-pulse h-8 bg-slate-200 dark:bg-slate-700 rounded"></div>
          ) : (
            <div>
              <div className="flex items-center">
                <div className="flex-grow relative">
                  <input
                    type="text"
                    value={showApiKey ? apiKey : maskApiKey(apiKey)}
                    readOnly
                    className="w-full px-2.5 py-1.5 border border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white font-mono text-xs focus:outline-none"
                  />
                </div>
                <button
                  onClick={toggleApiKeyVisibility}
                  className="ml-2 p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-slate-500 dark:text-slate-400 transition-colors"
                  aria-label={showApiKey ? "Hide API key" : "Show API key"}
                >
                  {showApiKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button
                  onClick={() => copyToClipboard(apiKey, 'legacy')}
                  className="ml-1 p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-slate-500 dark:text-slate-400 transition-colors"
                  aria-label="Copy API key to clipboard"
                >
                  {copied === 'legacy' ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                </button>
              </div>
              
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Include this key in the 'x-api-key' header when making requests. This key is derived from your session token.
              </p>
            </div>
          )}
        </div>
      )}

      {/* API Keys Management */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-medium text-gray-700 dark:text-gray-300">API Keys</h2>
          <button
            onClick={fetchApiKeys}
            className="inline-flex items-center text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            disabled={isLoading}
          >
            <RefreshCw size={14} className={`mr-1 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            <div className="animate-pulse h-12 bg-slate-200 dark:bg-slate-700 rounded"></div>
            <div className="animate-pulse h-12 bg-slate-200 dark:bg-slate-700 rounded"></div>
          </div>
        ) : (
          <>
            {/* Create new API key form */}
            <div className="mb-4 p-3 bg-gray-50 dark:bg-slate-700/30 rounded-md border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Create a new API key</h3>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="Enter key name..."
                  className="flex-grow px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-white text-sm focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none"
                />
                <button
                  onClick={createApiKey}
                  disabled={isCreating}
                  className="inline-flex items-center px-3 py-1.5 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white text-sm font-medium rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                >
                  {isCreating ? (
                    <>
                      <RefreshCw size={14} className="mr-1.5 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus size={14} className="mr-1.5" />
                      Create Key
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* API keys list */}
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {apiKeys.length === 0 ? (
                <div className="py-4 text-center text-sm text-slate-500 dark:text-slate-400">
                  You don't have any API keys yet. Create one to get started.
                </div>
              ) : (
                apiKeys.map((key) => (
                  <div key={key.id} className="py-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-medium text-sm text-slate-800 dark:text-white">
                        {key.name || 'API Key'}
                      </div>
                      {deleteKeyId === key.id ? (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-red-600 dark:text-red-400">Confirm delete?</span>
                          <button
                            onClick={() => deleteApiKey(key.id)}
                            disabled={isDeleting}
                            className="p-1 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 rounded"
                            aria-label="Confirm delete"
                          >
                            {isDeleting ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                          </button>
                          <button
                            onClick={() => setDeleteKeyId(null)}
                            className="p-1 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-400 rounded"
                            aria-label="Cancel delete"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteKeyId(key.id)}
                          className="text-slate-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400 transition-colors"
                          aria-label="Delete API key"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    
                    <div className="flex items-center mb-1">
                      <div className="flex-grow">
                        <code className="text-xs font-mono text-slate-500 dark:text-slate-400">
                          {key.preview || '••••••••'}
                        </code>
                      </div>
                      <button
                        onClick={() => copyToClipboard(key.preview, key.id)}
                        className="ml-1 p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-slate-500 dark:text-slate-400 transition-colors"
                        aria-label="Copy API key preview to clipboard"
                      >
                        {copied === key.id ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                      </button>
                    </div>
                    
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      Created: {formatDate(key.created_at)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* New API key modal */}
      {newApiKey && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg max-w-md w-full mx-4 p-5 border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Your New API Key</h3>
              <button
                onClick={closeNewKeyModal}
                className="text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                This is your new API key. Make sure to copy it now - you won't be able to see it again!
              </p>
              
              <div className="my-4 p-3 border border-amber-200 dark:border-amber-800/40 rounded-md bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-sm flex items-start">
                <AlertCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                <span>
                  This key will only be displayed once. If you lose it, you'll need to generate a new one.
                </span>
              </div>
              
              <div className="flex items-center">
                <div className="flex-grow relative">
                  <input
                    type="text"
                    value={showNewApiKey ? newApiKey.api_key : maskApiKey(newApiKey.api_key)}
                    readOnly
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white font-mono text-sm focus:outline-none"
                  />
                </div>
                <button
                  onClick={() => setShowNewApiKey(!showNewApiKey)}
                  className="ml-2 p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-slate-500 dark:text-slate-400 transition-colors"
                  aria-label={showNewApiKey ? "Hide API key" : "Show API key"}
                >
                  {showNewApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                <button
                  onClick={() => copyToClipboard(newApiKey.api_key, 'new')}
                  className="ml-1 p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-slate-500 dark:text-slate-400 transition-colors"
                  aria-label="Copy API key to clipboard"
                >
                  {copied === 'new' ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                </button>
              </div>
            </div>
            
            <div className="mt-5 flex justify-end">
              <button
                onClick={closeNewKeyModal}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700 text-white text-sm font-medium rounded shadow-sm"
              >
                I've copied my key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;